import { Controller } from "@hotwired/stimulus"
import { computeTrackBoundaries } from "../lib/track_boundary"

// F1 24/25 team ids are 0–9; F1 26 reissued the grid as 476–486 (per the
// official UDP spec appendix) so both ranges coexist here. Colors match the
// existing live-map palette (which differs slightly from
// leaderboard_controller.js by design); the F1 26 block mirrors the same
// palette in 476–484 and uses livery approximations for Audi / Cadillac.
const F1_TEAM_COLORS = {
  // F1 24 / F1 25
  0: "#27F4D2",
  1: "#E8002D",
  2: "#3671C6",
  3: "#FF8000",
  4: "#229971",
  5: "#64C4FF",
  6: "#B6BABD",
  7: "#1868DB",
  8: "#52E252",
  9: "#A6051A",
  // F1 26 (2026 Season Pack)
  476: "#27F4D2", // Mercedes
  477: "#E8002D", // Ferrari
  478: "#3671C6", // Red Bull
  479: "#FF8000", // Williams (matches existing live-map palette)
  480: "#229971", // Aston Martin
  481: "#64C4FF", // Alpine
  482: "#B6BABD", // RB
  483: "#1868DB", // Haas
  484: "#52E252", // McLaren
  485: "#00583E", // Audi (racing green over black)
  486: "#A8A8A8", // Cadillac (silver / black)
}

const WEATHER_NAMES = {
  0: "Clear",
  1: "Light Cloud",
  2: "Overcast",
  3: "Light Rain",
  4: "Heavy Rain",
  5: "Storm",
}

export default class extends Controller {
  static targets = ["svg"]
  static values = {
    centerLine: Array,
  }

  connect() {
    this.carPositions = {}
    this.prevPositions = {}
    this.trackPoints = []
    this.animationId = null
    this.lastTimestamp = 0
    this.width = 400
    this.height = 300
    this.padding = 20
    this.bounds = null

    // Session-level data from updates
    this.weather = null
    this.airTemp = null
    this.trackTemp = null
    this.safetyCarStatus = null
    this.trackedCarIndex = 0

    if (this.hasCenterLineValue && this.centerLineValue.length > 2) {
      this._initFromCenterLine()
    }
  }

  disconnect() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  update(event) {
    const data = event.detail
    if (!data.cars) return

    // Store session-level data
    if (data.weather !== undefined) this.weather = data.weather
    if (data.air_temperature !== undefined) this.airTemp = data.air_temperature
    if (data.track_temperature !== undefined)
      this.trackTemp = data.track_temperature
    if (data.safety_car_status !== undefined)
      this.safetyCarStatus = data.safety_car_status
    if (data.tracked_car_index !== undefined)
      this.trackedCarIndex = data.tracked_car_index

    this.prevPositions = { ...this.carPositions }

    const newPositions = {}
    for (const car of data.cars) {
      if (car.world_position_x === 0 && car.world_position_z === 0) continue

      newPositions[car.car_index] = {
        x: car.world_position_x,
        z: car.world_position_z,
        position: car.position,
        driverName: car.driver_name || `Car ${car.car_index}`,
        pitStatus: car.pit_status,
        teamId: car.team_id,
        carIndex: car.car_index,
      }
    }
    this.carPositions = newPositions

    // Only update bounds dynamically when no preloaded track (centerline bounds are fixed)
    if (!this._hasPreloadedTrack) {
      this.updateBounds()
    }
    this.collectTrackPoints(newPositions)

    if (!this.animationId) {
      this.lastTimestamp = performance.now()
      this.animate()
    }
  }

  updateBounds() {
    const positions = Object.values(this.carPositions)
    if (positions.length === 0) return

    const xs = positions.map((p) => p.x)
    const zs = positions.map((p) => p.z)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minZ = Math.min(...zs)
    const maxZ = Math.max(...zs)

    if (!this.bounds) {
      this.bounds = {
        minX,
        maxX,
        minZ,
        maxZ,
        rangeX: maxX - minX || 1,
        rangeZ: maxZ - minZ || 1,
      }
    } else {
      const lerp = 0.05
      this.bounds.minX =
        Math.min(this.bounds.minX, minX) * (1 - lerp) + minX * lerp
      this.bounds.maxX =
        Math.max(this.bounds.maxX, maxX) * (1 - lerp) + maxX * lerp
      this.bounds.minZ =
        Math.min(this.bounds.minZ, minZ) * (1 - lerp) + minZ * lerp
      this.bounds.maxZ =
        Math.max(this.bounds.maxZ, maxZ) * (1 - lerp) + maxZ * lerp
      this.bounds.rangeX = this.bounds.maxX - this.bounds.minX || 1
      this.bounds.rangeZ = this.bounds.maxZ - this.bounds.minZ || 1
    }
  }

  collectTrackPoints(positions) {
    if (this._hasPreloadedTrack) return

    const leader = Object.values(positions).find((p) => p.position === 1)
    if (!leader) return

    const last = this.trackPoints[this.trackPoints.length - 1]
    if (last) {
      const dx = leader.x - last.x
      const dz = leader.z - last.z
      if (dx * dx + dz * dz < 25) return
    }

    this.trackPoints.push({ x: leader.x, z: leader.z })
    if (this.trackPoints.length > 2000) {
      this.trackPoints = this.trackPoints.slice(-2000)
    }
  }

  _initFromCenterLine() {
    const cl = this.centerLineValue
    this._hasPreloadedTrack = true
    this._centerLineData = cl

    this.trackPoints = cl.map(([x, _y, z]) => ({ x, z }))

    // Compute bounds from centerline with uniform aspect ratio
    const xs = cl.map(([x]) => x)
    const zs = cl.map((p) => p[2])
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minZ = Math.min(...zs)
    const maxZ = Math.max(...zs)

    // Uniform scaling: fit the larger dimension so the track isn't stretched
    const rangeX = maxX - minX || 1
    const rangeZ = maxZ - minZ || 1
    const drawW = this.width - 2 * this.padding
    const drawH = this.height - 2 * this.padding
    const scaleX = drawW / rangeX
    const scaleZ = drawH / rangeZ
    const uniformScale = Math.min(scaleX, scaleZ)

    // Center the track within the viewport
    const usedW = rangeX * uniformScale
    const usedH = rangeZ * uniformScale
    const offsetX = (drawW - usedW) / 2
    const offsetZ = (drawH - usedH) / 2

    this.bounds = { minX, maxX, minZ, maxZ, rangeX, rangeZ }
    this._uniformScale = uniformScale
    this._drawOffset = { x: this.padding + offsetX, z: this.padding + offsetZ }

    this.render()
  }

  worldToScreen(x, z) {
    if (!this.bounds) return { sx: 0, sy: 0 }

    // Use uniform scaling when available (preloaded track) to preserve aspect ratio
    if (this._uniformScale) {
      const sx =
        this._drawOffset.x + (x - this.bounds.minX) * this._uniformScale
      const sy =
        this._drawOffset.z + (z - this.bounds.minZ) * this._uniformScale
      return { sx, sy }
    }

    const sx =
      this.padding +
      ((x - this.bounds.minX) / this.bounds.rangeX) *
        (this.width - 2 * this.padding)
    const sy =
      this.padding +
      ((z - this.bounds.minZ) / this.bounds.rangeZ) *
        (this.height - 2 * this.padding)
    return { sx, sy }
  }

  animate() {
    this.render()
    this.animationId = requestAnimationFrame(() => this.animate())
  }

  render() {
    if (!this.hasSvgTarget) return

    const cars = Object.entries(this.carPositions)
    if (cars.length === 0 && !this._hasPreloadedTrack) return

    let trackSurface = ""
    if (this._centerLineData && this._centerLineData.length > 2) {
      const cl = this._centerLineData
      // computeTrackBoundaries projects each boundary point through `project`
      // (x, y, z) → [screenX, screenY] and removes self-intersection loops, so
      // hand it world→screen and consume the returned [sx, sy] pairs directly.
      // worldToScreen here is a uniform affine map, so screen-space loop removal
      // is equivalent to world-space.
      const project = (wx, _wy, wz) => {
        const { sx, sy } = this.worldToScreen(wx, wz)
        return [sx, sy]
      }
      const { left: leftPts, right: rightPts } = computeTrackBoundaries(
        cl,
        project,
      )
      let surfD = `M${leftPts[0][0]},${leftPts[0][1]}`
      for (let i = 1; i < leftPts.length; i++)
        surfD += ` L${leftPts[i][0]},${leftPts[i][1]}`
      for (let i = rightPts.length - 1; i >= 0; i--)
        surfD += ` L${rightPts[i][0]},${rightPts[i][1]}`
      surfD += " Z"
      trackSurface = `<path d="${surfD}" fill="rgba(255,255,255,0.08)" stroke="none"/>`
    }

    let trackPath = ""
    if (this.trackPoints.length > 1) {
      const segments = this.trackPoints.map((p, i) => {
        const { sx, sy } = this.worldToScreen(p.x, p.z)
        return i === 0 ? `M${sx},${sy}` : `L${sx},${sy}`
      })
      trackPath = `<path d="${segments.join(" ")}" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>`
    }

    const carDots = cars
      .sort(([, a], [, b]) => b.position - a.position)
      .map(([, car]) => {
        const { sx, sy } = this.worldToScreen(car.x, car.z)
        const isTracked = car.carIndex === this.trackedCarIndex
        const color =
          F1_TEAM_COLORS[car.teamId] ||
          getComputedStyle(document.documentElement)
            .getPropertyValue("--app-text-muted")
            .trim()
        const strokeColor = isTracked ? "white" : "rgba(0,0,0,0.5)"
        const strokeWidth = isTracked ? 2 : 1
        const r = 7

        return (
          `<circle cx="${sx}" cy="${sy}" r="${r}" fill="${color}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>` +
          `<text x="${sx}" y="${sy + 3.5}" text-anchor="middle" fill="white" font-size="8" font-weight="bold" font-family="monospace" pointer-events="none">${car.position}</text>`
        )
      })
      .join("")

    // Weather/temperature overlay (top-left)
    let weatherOverlay = ""
    if (this.weather != null) {
      const weatherName = WEATHER_NAMES[this.weather] || ""
      const airStr = this.airTemp != null ? `Air ${this.airTemp}\u00B0C` : ""
      const trackStr =
        this.trackTemp != null ? `Track ${this.trackTemp}\u00B0C` : ""
      const tempStr = [airStr, trackStr].filter(Boolean).join("  ")
      weatherOverlay =
        `<text x="8" y="16" fill="rgba(255,255,255,0.6)" font-size="10" font-family="monospace">${weatherName}</text>` +
        (tempStr
          ? `<text x="8" y="28" fill="rgba(255,255,255,0.4)" font-size="9" font-family="monospace">${tempStr}</text>`
          : "")
    }

    // Safety car badge (top-right)
    let scOverlay = ""
    if (this.safetyCarStatus === 1) {
      scOverlay = `<rect x="355" y="4" width="38" height="18" rx="3" fill="rgba(234,179,8,0.3)"/><text x="374" y="16" text-anchor="middle" fill="#eab308" font-size="10" font-weight="bold" font-family="monospace">SC</text>`
    } else if (this.safetyCarStatus === 2) {
      scOverlay = `<rect x="348" y="4" width="46" height="18" rx="3" fill="rgba(234,179,8,0.3)"/><text x="371" y="16" text-anchor="middle" fill="#eab308" font-size="10" font-weight="bold" font-family="monospace">VSC</text>`
    } else if (this.safetyCarStatus === 3) {
      scOverlay = `<rect x="350" y="4" width="42" height="18" rx="3" fill="rgba(239,68,68,0.3)"/><text x="371" y="16" text-anchor="middle" fill="#ef4444" font-size="10" font-weight="bold" font-family="monospace">RED</text>`
    }

    this.svgTarget.innerHTML = `${trackSurface}${trackPath}${carDots}${weatherOverlay}${scOverlay}`
  }
}
